<script lang="ts">
import type { ButtonProps } from '#layers/ui/public';
import type { CollapsibleProps } from '#ui/types';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { BLOCK_CONTROL_SIZE_OPTIONS } from '#layers/blocks/constants/ui-options';

/** Props exposed by the Collapsible block. */
interface CollapsibleBlockProps {
	/** Trigger label shown above the collapsible content. */
	label?: ButtonProps['label'];
	/** Optional trigger icon. */
	icon?: ButtonProps['icon'];
	/** Initially opened state when the block is uncontrolled. */
	defaultOpen?: CollapsibleProps['defaultOpen'];
	/** Disables the collapsible interaction. */
	disabled?: CollapsibleProps['disabled'];
	/** Allows toggling the section; when false the content stays open and the trigger is disabled. */
	collapsible?: boolean;
	/** Whether closed content should be removed from the DOM. */
	unmountOnHide?: CollapsibleProps['unmountOnHide'];
	/** Trigger button accent color. */
	color?: ButtonProps['color'];
	/** Trigger button visual style. */
	variant?: ButtonProps['variant'];
	/** Trigger button size. */
	size?: ButtonProps['size'];
}

/** Model value forwarded to DCollapsible. */
type CollapsibleBlockOpen = CollapsibleProps['open'];

const __DsBlock = {
	type: 'container',
	name: '$t:blocks.collapsible.name',
	description: '$t:blocks.collapsible.description',
	icon: 'lucide:chevrons-up-down',
	category: 'layout',
	tags: ['layout', 'form-container'],
	interactive: true,
	slots: ['default'],
	formSchema: {
		chrome: {
			section: {},
		},
	},
	options: [
		{
			name: 'open',
			model: true,
		},
		{
			name: 'openModifiers',
			hidden: true,
		},
		{
			name: 'label',
			label: '$t:blocks.common.props.label',
		},
		{
			name: 'icon',
			label: '$t:blocks.common.props.icon',
			block: BuiltinBlockIds.InputIcon,
			nullable: true,
		},
		{
			name: 'defaultOpen',
			label: '$t:blocks.collapsible.props.default_open',
		},
		{
			kind: FormSchemaKind.Presentational,
			block: BuiltinBlockIds.FormSeparator,
			props: {
				label: '$t:common.appearance',
			},
		},
		{
			name: 'color',
			label: '$t:blocks.common.props.color',
			block: BuiltinBlockIds.InputSemanticColor,
		},
		{
			name: 'variant',
			label: '$t:blocks.common.props.variant',
			type: 'enum',
			values: ['solid', 'outline', 'soft', 'subtle', 'ghost', 'link'],
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			kind: FormSchemaKind.Presentational,
			block: BuiltinBlockIds.FormSeparator,
			props: {
				label: '$t:common.state',
			},
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'unmountOnHide',
			label: '$t:blocks.collapsible.props.unmount_on_hide',
		},
	],
}</script>

<script lang="ts" setup>
import { computed } from 'vue';
import { defineBlock, FormSchemaKind } from '#layers/block-engine/public';
import { useTranslationUtils } from '#layers/i18n/public';

const {
	label = '$t:blocks.collapsible.default_label',
	collapsible = true,
	color = 'neutral',
	size = 'md',
	unmountOnHide = false,
	variant = 'ghost',
	disabled,
} = defineProps<CollapsibleBlockProps>();

const open = defineModel<CollapsibleBlockOpen>('open', { default: undefined });

const effectiveOpen = computed<CollapsibleBlockOpen>({
	get: () => collapsible ? open.value : true,
	set: (value) => {
		open.value = value;
	},
});

;

const { translateString } = useTranslationUtils();
const triggerLabel = computed(() => translateString(label));
const isDisabled = computed(() => disabled || !collapsible);
</script>

<template>
	<DCollapsible
		v-model:open="effectiveOpen"
		class="col-span-full"
		:default-open
		:disabled="isDisabled"
		:unmount-on-hide
	>
		<template #default="{ open: isOpen }">
			<DButton
				:label="triggerLabel"
				:icon
				:color
				:variant
				:size
				:disabled="isDisabled"
				:trailing-icon="isOpen ? 'lucide:chevron-up' : 'lucide:chevron-down'"
				block
			/>
		</template>

		<template #content>
			<div class="pt-(--block-spacing)">
				<slot />
			</div>
		</template>
	</DCollapsible>
</template>
